import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import AdminPageShell from '../../components/admin/AdminPageShell';
import api from '../../lib/api';
import { ADMIN_COMPACT_BUTTON, ADMIN_HEADER_ACTIONS } from '../../styles/adminStyles';

function getQueryParam(search, name) {
  const s = String(search || '').replace(/^\?/, '');
  if (!s) return null;
  const parts = s.split('&');
  for (let i = 0; i < parts.length; i += 1) {
    const p = parts[i];
    const eq = p.indexOf('=');
    const key = decodeURIComponent(eq >= 0 ? p.slice(0, eq) : p);
    if (key === name) {
      return decodeURIComponent(eq >= 0 ? p.slice(eq + 1) : '');
    }
  }
  return null;
}

function orgIdOf(org) {
  if (!org) return null;
  const id = org.id != null ? org.id : org.Id;
  return id != null ? Number(id) : null;
}

function formatOrgRole(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'owner') return 'Владелец';
  if (r === 'facilityadmin') return 'Сотрудник';
  if (r === 'platformadmin') return 'Админ платформы';
  return role || '—';
}

class OrganizationStaff extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    location: PropTypes.object,
    userRole: PropTypes.string,
    userId: PropTypes.number,
    centers: PropTypes.array,
  };

  static defaultProps = {
    location: { search: '' },
    userRole: null,
    userId: null,
    centers: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      orgs: [],
      selectedOrgId: '',
      members: [],
      loading: false,
      membersLoading: false,
      error: '',
      saved: '',
      emailInput: '',
      selectedComplexIds: [],
    };
  }

  componentDidMount() {
    this.loadOrgs();
  }

  isPlatformAdmin = () => this.props.userRole === 'administrator';

  selectedOrg = () => {
    const id = Number(this.state.selectedOrgId);
    return (this.state.orgs || []).find((o) => orgIdOf(o) === id) || null;
  };

  isCurrentUserOrgOwner = () => {
    const myId = this.props.userId;
    if (!myId) return false;
    return (this.state.members || []).some((m) => {
      const role = String(m.role || m.Role || '').toLowerCase();
      const uid = Number(m.userId || m.UserId);
      return role === 'owner' && uid === Number(myId);
    });
  };

  canManageStaff = () => this.isPlatformAdmin() || this.isCurrentUserOrgOwner();

  orgComplexIds = () => {
    const org = this.selectedOrg();
    const raw = (org && (org.complexIds || org.ComplexIds)) || [];
    return (Array.isArray(raw) ? raw : []).map(Number).filter((n) => n > 0);
  };

  centerName = (complexId) => {
    const center = (this.props.centers || []).find((c) => Number(c.id) === Number(complexId));
    if (center && center.name) return center.name;
    const org = this.selectedOrg();
    const ids = (org && (org.complexIds || org.ComplexIds)) || [];
    const names = (org && (org.complexNames || org.ComplexNames)) || [];
    const idx = ids.findIndex((id) => Number(id) === Number(complexId));
    if (idx >= 0 && names[idx]) return names[idx];
    return `#${complexId}`;
  };

  loadOrgs = async () => {
    this.setState({ loading: true, error: '' });
    const search = (this.props.location && this.props.location.search) || '';
    const complexId = getQueryParam(search, 'complexId');
    const orgIdParam = getQueryParam(search, 'orgId');
    try {
      let list = await api.getOrganizations();
      list = Array.isArray(list) ? list : [];
      let selected = orgIdParam ? String(orgIdParam) : '';

      if (complexId) {
        try {
          const org = await api.getOrganizationByComplex(Number(complexId));
          const id = orgIdOf(org);
          if (id && !list.some((o) => orgIdOf(o) === id)) {
            list = [org, ...list];
          }
          if (id) selected = String(id);
        } catch (err) {
          if (!list.length) {
            this.setState({
              loading: false,
              error: err.message || 'Не удалось открыть организацию комплекса',
              orgs: [],
            });
            return;
          }
        }
      }

      if (!selected && list.length) {
        selected = String(orgIdOf(list[0]));
      }

      const org = list.find((o) => String(orgIdOf(o)) === String(selected));
      const complexIds = ((org && (org.complexIds || org.ComplexIds)) || []).map(Number).filter((n) => n > 0);
      this.setState({
        orgs: list,
        selectedOrgId: selected,
        loading: false,
        selectedComplexIds: complexIds,
        emailInput: '',
      }, this.loadMembers);
    } catch (err) {
      this.setState({
        loading: false,
        error: err.message || 'Не удалось загрузить организации',
        orgs: [],
      });
    }
  };

  loadMembers = async () => {
    const orgId = Number(this.state.selectedOrgId);
    if (!orgId) {
      this.setState({ members: [] });
      return;
    }
    this.setState({ membersLoading: true, error: '' });
    try {
      const members = await api.getOrganizationMembers(orgId);
      this.setState({
        members: Array.isArray(members) ? members : [],
        membersLoading: false,
      });
    } catch (err) {
      this.setState({
        membersLoading: false,
        members: [],
        error: err.message || 'Не удалось загрузить сотрудников',
      });
    }
  };

  onSelectOrg = (event) => {
    const selectedOrgId = event.target.value;
    const org = (this.state.orgs || []).find((o) => String(orgIdOf(o)) === String(selectedOrgId));
    const complexIds = ((org && (org.complexIds || org.ComplexIds)) || []).map(Number).filter((n) => n > 0);
    this.setState({
      selectedOrgId,
      selectedComplexIds: complexIds,
      emailInput: '',
      saved: '',
    }, this.loadMembers);
  };

  orgClubs = () => {
    const org = this.selectedOrg();
    const ids = (org && (org.complexIds || org.ComplexIds)) || [];
    const names = (org && (org.complexNames || org.ComplexNames)) || [];
    return (Array.isArray(ids) ? ids : []).map((id, idx) => ({
      id: Number(id),
      name: names[idx] || this.centerName(id),
    })).filter((c) => c.id > 0);
  };

  toggleClub = (complexId) => {
    const id = Number(complexId);
    this.setState((prev) => {
      const has = (prev.selectedComplexIds || []).some((x) => Number(x) === id);
      const next = has
        ? prev.selectedComplexIds.filter((x) => Number(x) !== id)
        : prev.selectedComplexIds.concat([id]);
      return { selectedComplexIds: next };
    });
  };

  staffEmail = () => (this.state.emailInput || '').trim();

  addStaff = async () => {
    const orgId = Number(this.state.selectedOrgId);
    const email = this.staffEmail();
    if (!orgId || !email || email.indexOf('@') < 1) {
      this.setState({ error: 'Укажите email сотрудника' });
      return;
    }
    let complexIds = (this.state.selectedComplexIds || []).map(Number).filter((n) => n > 0);
    if (!complexIds.length) {
      complexIds = this.orgComplexIds();
    }
    this.setState({ membersLoading: true, error: '', saved: '' });
    try {
      await api.addOrganizationMember(orgId, {
        email,
        role: 'FacilityAdmin',
        complexIds,
      });
      this.setState({
        emailInput: '',
        saved: 'Сотрудник добавлен',
      });
      this.loadMembers();
    } catch (err) {
      this.setState({
        membersLoading: false,
        error: err.message || 'Пользователь не найден',
      });
    }
  };

  assignOwner = async () => {
    const orgId = Number(this.state.selectedOrgId);
    const email = this.staffEmail();
    if (!orgId || !email || email.indexOf('@') < 1) {
      this.setState({ error: 'Укажите email владельца' });
      return;
    }
    const currentOwners = (this.state.members || []).filter((m) => {
      const role = String(m.role || m.Role || '');
      return role.toLowerCase() === 'owner';
    });
    if (currentOwners.length > 0) {
      const names = currentOwners
        .map((m) => m.userName || m.UserName || m.userEmail || m.UserEmail || 'текущий владелец')
        .join(', ');
      if (!window.confirm(`${names} перестанет быть владельцем и станет сотрудником. Сделать владельцем ${email}?`)) {
        return;
      }
    }
    this.setState({ membersLoading: true, error: '', saved: '' });
    try {
      await api.assignOrganizationOwner(orgId, email);
      this.setState({
        emailInput: '',
        saved: 'Владелец назначен',
      });
      this.loadMembers();
    } catch (err) {
      this.setState({
        membersLoading: false,
        error: err.message || 'Пользователь не найден',
      });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      orgs,
      selectedOrgId,
      members,
      loading,
      membersLoading,
      error,
      saved,
      emailInput,
      selectedComplexIds,
    } = this.state;
    const org = this.selectedOrg();
    const orgName = org && (org.name || org.Name);
    const canManage = this.canManageStaff();
    const clubs = this.orgClubs();
    const emailReady = (emailInput || '').trim().indexOf('@') >= 1;

    return (
      <AdminPageShell
        title="Сотрудники"
        subtitle="Кто управляет клубами сети: владелец и сотрудники на стойке. Это не клиенты и не staff из YClients."
      >
        {loading ? (
          <div className={classes.loader}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <Paper className={classes.paper} elevation={2}>
            {(orgs || []).length > 1 && (
              <FormControl className={classes.orgSelect}>
                <InputLabel htmlFor="org-staff-select">Организация</InputLabel>
                <Select
                  inputProps={{ id: 'org-staff-select' }}
                  value={selectedOrgId}
                  onChange={this.onSelectOrg}
                >
                  {(orgs || []).map((item) => (
                    <MenuItem key={orgIdOf(item)} value={String(orgIdOf(item))}>
                      {item.name || item.Name || 'Организация'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {orgName && (orgs || []).length <= 1 && (
              <Typography variant="body2" className={classes.orgLabel}>
                {orgName || 'Организация'}
              </Typography>
            )}
            {!orgs.length && (
              <Typography color="textSecondary">
                Нет доступных организаций. Админ платформы может открыть страницу с карточки клуба.
              </Typography>
            )}
            {error && (
              <Typography color="error" className={classes.message}>{error}</Typography>
            )}
            {saved && (
              <Typography color="primary" className={classes.message}>{saved}</Typography>
            )}
            {membersLoading ? (
              <div className={classes.loader}>
                <CircularProgress size={24} />
              </div>
            ) : (
              <Table className={classes.table} padding="dense">
                <TableHead>
                  <TableRow>
                    <TableCell>Сотрудник</TableCell>
                    <TableCell>Роль</TableCell>
                    <TableCell>Клубы</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(members || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="textSecondary">Пока никого нет — назначьте владельца или сотрудника</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(members || []).map((m) => {
                    const ids = m.complexIds || m.ComplexIds || [];
                    return (
                      <TableRow key={m.id || m.Id || `${m.userId}-${m.role}`}>
                        <TableCell>
                          {m.userName || m.UserName || 'Без имени'}
                        </TableCell>
                        <TableCell>{formatOrgRole(m.role || m.Role)}</TableCell>
                        <TableCell>
                          {(ids || []).map((cid) => this.centerName(cid)).join(', ') || '—'}
                        </TableCell>
                        <TableCell>{m.userEmail || m.UserEmail || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {canManage && selectedOrgId && (
              <div className={classes.addBlock}>
                <Typography variant="subtitle1" className={classes.addTitle}>
                  Добавить сотрудника
                </Typography>
                <Typography variant="body2" color="textSecondary" className={classes.addHint}>
                  Введите точный email человека, который уже зарегистрирован в PLAYBASE.
                  Поиск по базе не выполняется: либо совпадение один-в-один, либо «пользователь не найден».
                </Typography>
                <TextField
                  margin="dense"
                  label="Email"
                  placeholder="name@example.com"
                  type="email"
                  value={emailInput}
                  onChange={(e) => this.setState({ emailInput: e.target.value, error: '' })}
                  fullWidth
                  autoComplete="off"
                />
                {clubs.length > 1 && (
                  <FormControl component="fieldset" className={classes.clubs}>
                    <FormLabel component="legend">В каких клубах сможет работать</FormLabel>
                    <FormGroup>
                      {clubs.map((club) => (
                        <FormControlLabel
                          key={club.id}
                          control={
                            <Checkbox
                              color="primary"
                              checked={(selectedComplexIds || []).some((id) => Number(id) === club.id)}
                              onChange={() => this.toggleClub(club.id)}
                            />
                          }
                          label={club.name}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                )}
                {clubs.length === 1 && (
                  <Typography variant="body2" className={classes.oneClub}>
                    Клуб: {clubs[0].name}
                  </Typography>
                )}
                <div className={classes.formRow}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    className={classes.compactButton}
                    onClick={this.addStaff}
                    disabled={!!membersLoading || !emailReady}
                  >
                    Добавить сотрудником
                  </Button>
                  {this.isPlatformAdmin() && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      className={classes.compactButton}
                      onClick={this.assignOwner}
                      disabled={!!membersLoading || !emailReady}
                    >
                      Сделать владельцем
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Paper>
        )}
      </AdminPageShell>
    );
  }
}

const styles = (theme) => ({
  paper: {
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    maxWidth: '100%',
  },
  orgSelect: {
    minWidth: 280,
    marginBottom: theme.spacing(2),
  },
  orgLabel: {
    marginBottom: theme.spacing(2),
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  message: {
    marginBottom: theme.spacing(1),
  },
  table: {
    '& thead th': {
      backgroundColor: '#f8f9fa',
      fontWeight: 600,
    },
  },
  formRow: {
    ...ADMIN_HEADER_ACTIONS,
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    '& .MuiButton-root': {
      ...ADMIN_COMPACT_BUTTON,
    },
  },
  compactButton: {
    ...ADMIN_COMPACT_BUTTON,
  },
  addBlock: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e9ecef',
  },
  addTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  addHint: {
    marginBottom: theme.spacing(1),
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    margin: `${theme.spacing(1)}px 0`,
    maxHeight: 220,
    overflowY: 'auto',
    border: '1px solid #e9ecef',
    borderRadius: 4,
  },
  resultItem: {
    textAlign: 'left',
    background: '#fff',
    border: 0,
    borderBottom: '1px solid #f1f3f5',
    padding: '8px 12px',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': {
      background: '#f8f9fa',
    },
  },
  resultItemActive: {
    textAlign: 'left',
    background: '#e8f4fd',
    border: 0,
    borderBottom: '1px solid #f1f3f5',
    padding: '8px 12px',
    cursor: 'pointer',
    font: 'inherit',
  },
  resultName: {
    display: 'block',
    fontWeight: 600,
  },
  resultMeta: {
    display: 'block',
    color: '#6c757d',
    fontSize: '0.8125rem',
  },
  clubs: {
    marginTop: theme.spacing(2),
  },
  oneClub: {
    marginTop: theme.spacing(1),
  },
});

const Styled = withStyles(styles)(OrganizationStaff);

export default connect((state) => ({
  userRole: state.auth && state.auth.role,
  userId: state.auth && state.auth.userId,
  centers: state.centers || [],
}))(Styled);
